import { Fragment, useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

import { ITicketInsightTopic } from "../../../models/Tickets/TicketInsight";
import { formatHours } from "../TicketAvgResponseTable";
import InsightTicketRow from "./InsightTicketRow";
import { Card, EmptyState, StackedBar, TrendBadge } from "./InsightWidgets";
import {
  categoryBarClassMap,
  categoryByName,
  categoryLabels,
  CategoryPill,
} from "./insightMeta";

interface TopicsCardProps {
  topics: ITicketInsightTopic[];
  hasPreviousPeriod: boolean;
}

/**
 * The model's topic labels, ranked by volume. Each row expands to the tickets
 * behind it, so "eBay Listing Sync: 14" turns into something you can act on.
 */
export default function TopicsCard({ topics, hasPreviousPeriod }: TopicsCardProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const visible = showAll ? topics : topics.slice(0, 12);
  const max = Math.max(0, ...topics.map((t) => t.count));

  return (
    <Card
      title="Topics"
      description={
        hasPreviousPeriod
          ? "What tickets are about, with the change against the previous period of the same length."
          : "What tickets are about. Pick a date range to see trends against the previous period."
      }
      action={
        topics.length > 12 ? (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs text-orange-700 hover:underline"
          >
            {showAll ? "Show top 12" : `Show all ${topics.length}`}
          </button>
        ) : undefined
      }
    >
      {topics.length === 0 ? (
        <EmptyState>No topics yet - analyse some tickets first.</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500">
                <th className="w-6 py-1" />
                <th className="py-1 pr-3">Topic</th>
                <th className="py-1 pr-3">Mix</th>
                <th className="py-1 pr-3 text-right">Tickets</th>
                {hasPreviousPeriod ? <th className="py-1 pr-3 text-right">Trend</th> : null}
                <th className="py-1 pr-3 text-right">Active</th>
                <th className="py-1 pr-3 text-right">Customers</th>
                <th className="py-1 pr-3 text-right">Negative</th>
                <th className="py-1 pr-3 text-right">Urgency</th>
                <th className="py-1 text-right">Avg resolve</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((topic) => {
                const open = expanded === topic.topic;
                const segments = Object.entries(topic.categories).map(
                  ([name, value]) => {
                    const category = categoryByName[name];
                    return {
                      key: name,
                      value,
                      className:
                        category !== undefined
                          ? categoryBarClassMap[category]
                          : "bg-gray-300",
                      title: `${
                        category !== undefined ? categoryLabels[category] : name
                      }: ${value}`,
                    };
                  },
                );

                return (
                  <Fragment key={topic.topic}>
                    <tr
                      onClick={() => setExpanded(open ? null : topic.topic)}
                      className={clsx(
                        "cursor-pointer hover:bg-gray-50",
                        open && "bg-orange-50/60",
                      )}
                    >
                      <td className="py-2 text-gray-400">
                        {open ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">
                            {topic.topic}
                          </span>
                          <CategoryPill category={topic.dominantCategory} />
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="w-28">
                          <div className="mb-0.5 h-1.5 overflow-hidden rounded bg-gray-100">
                            <div
                              className="h-full rounded bg-orange-400"
                              style={{
                                width: `${max ? (topic.count / max) * 100 : 0}%`,
                              }}
                            />
                          </div>
                          <StackedBar segments={segments} className="h-1.5" />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                        {topic.count}
                      </td>
                      {hasPreviousPeriod ? (
                        <td className="py-2 pr-3 text-right">
                          <TrendBadge
                            current={topic.count}
                            previous={topic.previousCount}
                          />
                        </td>
                      ) : null}
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {topic.active}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {topic.customerCount}
                      </td>
                      <td
                        className={clsx(
                          "py-2 pr-3 text-right tabular-nums",
                          topic.negativeCount > 0 && "text-rose-700",
                        )}
                      >
                        {topic.negativeCount}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">
                        {topic.avgUrgency.toFixed(1)}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {formatHours(topic.avgResolutionHours)}
                      </td>
                    </tr>
                    {open ? (
                      <tr>
                        <td />
                        <td colSpan={hasPreviousPeriod ? 9 : 8} className="pb-3">
                          <div className="divide-y divide-gray-100 rounded border border-gray-200 bg-white px-3">
                            {topic.tickets.map((ticket) => (
                              <InsightTicketRow
                                key={ticket.ticketId}
                                ticket={ticket}
                              />
                            ))}
                            {topic.count > topic.tickets.length ? (
                              <p className="py-2 text-xs text-gray-500">
                                Showing {topic.tickets.length} of {topic.count}.
                                Active tickets are listed first.
                              </p>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
